import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@/state';
import { AsyncThunk } from '@reduxjs/toolkit';
import QueryTransaction from '@interfaces/transaction';

interface UseAutoFetchPaginationOptions {
	/** Whether the API has more pages of data available to fetch */
	hasMore: boolean;
	/** Cursor token for fetching the next page of data */
	nextCursor: string | null;
	/** Whether a request to fetch more data is currently in progress (prevents duplicate requests) */
	isLoadingMore: boolean;
	baseQuery: QueryTransaction;
	fetchMoreAction: AsyncThunk<any, QueryTransaction, any>;
	enabled?: boolean;
}

/**
 * Custom hook to automatically fetch more data when pagination indicates more data is available.
 * This allows the first page to display immediately while fetching remaining pages in the background.
 */
export function useAutoFetchPagination({
	hasMore,
	nextCursor,
	isLoadingMore,
	baseQuery,
	fetchMoreAction,
	enabled = true,
}: UseAutoFetchPaginationOptions) {
	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		if (enabled && hasMore && nextCursor && !isLoadingMore) {
			dispatch(
				fetchMoreAction({
					...baseQuery,
					cursor: nextCursor,
				})
			);
		}
	}, [enabled, hasMore, nextCursor, isLoadingMore, dispatch, fetchMoreAction, baseQuery]);
}

export default useAutoFetchPagination;
